import React from 'react';
import { ArrowUp, ArrowDown, ChevronsUpDown, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { COLUMN_BY_ID, formatStamp, formatBool } from '../columns';

const StatusPill = ({ row }) => {
  const isVoid = row.status === 'V';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        isVoid ? 'bg-red-900/40 text-red-300 border border-red-800/60'
               : 'bg-green-900/40 text-green-300 border border-green-800/60'
      }`}
      title={row.statusLabel}
    >
      {isVoid ? 'Voided' : 'Replacement'}
    </span>
  );
};

const CarrierPill = ({ carrier }) => {
  const tone = carrier === 'UPS' ? 'bg-amber-900/30 text-amber-300'
    : carrier === 'FedEx' ? 'bg-purple-900/30 text-purple-300'
    : 'bg-slate-700/50 text-slate-400';
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${tone}`}>{carrier}</span>;
};

/** Table cells get a fixed readable format; the CSV has its own options. */
const renderCell = (row, columnId) => {
  const col = COLUMN_BY_ID[columnId];
  const raw = row[columnId];

  if (col?.type === 'status') return <StatusPill row={row} />;
  if (columnId === 'carrier') return <CarrierPill carrier={raw} />;

  if (raw === null || raw === undefined || raw === '') {
    return <span className="text-slate-600">—</span>;
  }

  switch (col?.type) {
    case 'datetime':
      return <span className="tabular-nums">{formatStamp(raw, 'iso')}</span>;
    case 'date':
      return <span className="tabular-nums">{formatStamp(raw, 'iso', { dateOnly: true })}</span>;
    case 'bool':
      return (
        <span className={raw ? 'text-green-400' : 'text-amber-400'}>
          {formatBool(raw, 'yes_no')}
        </span>
      );
    case 'tracking':
      return <span className="font-mono text-xs">{raw}</span>;
    case 'number':
      return <span className="tabular-nums">{raw}</span>;
    default:
      return String(raw);
  }
};

const VoidTable = ({
  rows, visibleColumns, sort, onSortChange,
  page, pageSize, onPageChange, onPageSizeChange, isUnfiltered,
}) => {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);

  const toggleSort = (columnId) => {
    if (sort.columnId === columnId) {
      onSortChange({ columnId, direction: sort.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      onSortChange({ columnId, direction: 'asc' });
    }
  };

  const SortIcon = ({ columnId }) => {
    if (sort.columnId !== columnId) return <ChevronsUpDown className="w-3 h-3 text-slate-600" />;
    return sort.direction === 'asc'
      ? <ArrowUp className="w-3 h-3 text-blue-400" />
      : <ArrowDown className="w-3 h-3 text-blue-400" />;
  };

  if (!rows.length) {
    return (
      <div className="flex-1 min-h-0 bg-slate-800 border border-slate-700 rounded-lg p-12 text-center flex items-center justify-center">
        <p className="text-slate-400">
          {isUnfiltered
            ? 'No labels were voided in this date range.'
            : 'No rows match the current filters.'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden flex flex-col">
      {/* The only scrolling region on the page. */}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/70 sticky top-0 z-10">
            <tr>
              {visibleColumns.map((id) => {
                const col = COLUMN_BY_ID[id];
                if (!col) return null;
                return (
                  <th
                    key={id}
                    scope="col"
                    className={`px-3 py-2.5 text-left font-medium text-slate-400 whitespace-nowrap ${col.width || ''}`}
                  >
                    <button
                      onClick={() => toggleSort(id)}
                      className="flex items-center gap-1.5 hover:text-slate-200 transition-colors"
                    >
                      <span>{col.label}</span>
                      <SortIcon columnId={id} />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {pageRows.map((row, i) => (
              <tr
                key={`${row.orderNumber}-${row.masterTrackingNumber}-${start + i}`}
                className={`hover:bg-slate-700/30 ${row.status === 'V' ? '' : 'bg-slate-800/40'}`}
              >
                {visibleColumns.map((id) => (
                  <td key={id} className="px-3 py-2 text-slate-300 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {renderCell(row, id)}
                      {id === 'shipDate' && row.outsideRequestedRange && (
                        <span
                          title="This row's ship date falls outside the requested range. The upstream filter matches the void's ship date and returns the replacement whenever it shipped."
                          className="text-amber-400"
                        >
                          <AlertTriangle className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-700 bg-slate-800/80">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span>Rows per page</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="bg-slate-900 border border-slate-600 text-slate-200 rounded px-2 py-1 text-xs
                       focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {[25, 50, 100, 250, 500].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className="ml-2">
            {(start + 1).toLocaleString()}–{Math.min(start + pageSize, rows.length).toLocaleString()} of{' '}
            {rows.length.toLocaleString()}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(safePage - 1)}
            disabled={safePage <= 1}
            className="p-1.5 rounded text-slate-400 hover:bg-slate-700 hover:text-slate-200
                       disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-slate-400 px-2">
            Page {safePage} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(safePage + 1)}
            disabled={safePage >= totalPages}
            className="p-1.5 rounded text-slate-400 hover:bg-slate-700 hover:text-slate-200
                       disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoidTable;
